import { CreditCardType, SubscriptionPeriod } from '@anm/api/modules/subscription';
import { BillingPeriodsField } from '@anm/components/form/fields';
import CardDetailsCouponBlock from '@anm/components/subscription/CardDetailsCouponBlock';
import capitalize from '@anm/helpers/string/capitalize';
import getPeriodName from '@anm/helpers/subscription/getPeriodName';
import { ValidationErrors } from 'final-form';
import values from 'lodash/fp/values';
import { useCallback, useEffect, FC } from 'react';

import CouponEditCardBlock from '../CouponEditCardBlock';
import { DiscountInfoBlockProps } from '../DiscountInfoBlock';
import PredictPrice from '../PredictPrice';
import SecurePurchaseBlock from '../SecurePurchaseBlock';
import SubscriptionTitle from '../SubscriptionTitle';
import UpgradeBlockAlert from '../UpgradeBlockAlert';

import { useCardViewChanger, usePricePrediction } from './hooks';

export type UpgradeSubscriptionDiscount = Pick<
  DiscountInfoBlockProps,
  'amountDiscount' | 'percentsDiscount' | 'credit'
> & {
  error?: string;
};

export type UpgradeSubscriptionBlockProps = {
  sslId: string;
  coupon: string;
  planName: string;
  cardType: CreditCardType;
  cardLast4: string;
  formErrors: ValidationErrors;
  annualPrice: number;
  monthlyPrice: number;
  selectedPeriod: SubscriptionPeriod;
  isSubmitPending: boolean;
  isPredictPricePending: boolean;
  errors?: string[];
  discount?: UpgradeSubscriptionDiscount;
  totalPrice?: number;
  disablePeriod?: SubscriptionPeriod;
  showEditCardBlock?: boolean;
  onPurchase: () => void;
  onCouponApply: (coupon: string) => void;
  onPeriodChange: (toPeriod: SubscriptionPeriod) => void;
};

const UpgradeSubscriptionBlock: FC<UpgradeSubscriptionBlockProps> = ({
  sslId,
  coupon,
  errors = [],
  planName,
  discount,
  cardType,
  cardLast4,
  formErrors,
  totalPrice,
  annualPrice,
  monthlyPrice,
  disablePeriod,
  selectedPeriod,
  isSubmitPending,
  showEditCardBlock,
  isPredictPricePending,
  onPurchase,
  onCouponApply,
  onPeriodChange
}) => {
  const [showEditCard, handleCardChangeToEditView] = useCardViewChanger(showEditCardBlock);
  const handleCouponApply = useCallback(() => {
    onCouponApply(coupon);
  }, [coupon]);

  const [selectedFullPlanPrice, annualDiscount, selectedPlanPrice] = usePricePrediction({
    annualPrice,
    monthlyPrice,
    period: selectedPeriod
  });

  useEffect(() => {
    onPeriodChange(selectedPeriod);
  }, [selectedPeriod]);

  const modalTitle = disablePeriod ? `Switch to ${planName} Plan ${capitalize(selectedPeriod)}` : `Get ${planName}`;

  return (
    <>
      <SubscriptionTitle>{modalTitle}</SubscriptionTitle>
      <BillingPeriodsField {...{ annualPrice, monthlyPrice, disablePeriod }} />

      {!showEditCard && (
        <CardDetailsCouponBlock
          {...{ coupon, cardType, cardLast4 }}
          onChangeCard={handleCardChangeToEditView}
          onCouponApply={handleCouponApply}
        />
      )}

      {showEditCard && <CouponEditCardBlock onCouponApply={handleCouponApply} />}

      <PredictPrice
        {...discount}
        planName={planName}
        planPrice={selectedPlanPrice}
        fullPlanPrice={selectedFullPlanPrice}
        isPending={isPredictPricePending}
        totalPrice={totalPrice}
        periodName={selectedPeriod}
        annualDiscount={annualDiscount}
      />

      <UpgradeBlockAlert errors={[...values(formErrors), ...errors, discount?.error].filter(Boolean)} />

      <SecurePurchaseBlock {...{ sslId, onPurchase }} isPending={isSubmitPending}>
        Purchase {getPeriodName(selectedPeriod)}
      </SecurePurchaseBlock>
    </>
  );
};
export default UpgradeSubscriptionBlock;
